import { useEffect, useState } from 'react';
import { Card, Row, Col, Button, Badge, Alert, Spinner, Table, Modal, Form } from 'react-bootstrap';
import { api } from '../api/client';
import { formatDate } from '../shared/dateFormat';

export default function StudentClasses() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [detail, setDetail] = useState(null);
  const [review, setReview] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setRows(await api.get('/student/classes')); }
    catch (e) { setMsg({ type: 'danger', text: e.message }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const payNext = async (hireId) => {
    if (!window.confirm('Pay next month\'s fee from your wallet?')) return;
    try {
      await api.post(`/student/hires/${hireId}/pay-next-month`);
      setMsg({ type: 'success', text: 'Next month paid.' });
      await load();
    } catch (e) { setMsg({ type: 'danger', text: e.message }); }
  };

  const endHire = async (hireId) => {
    if (!window.confirm('End this hire? You won\'t be billed for further months.')) return;
    try {
      await api.post(`/student/hires/${hireId}/end`);
      await load();
    } catch (e) { setMsg({ type: 'danger', text: e.message }); }
  };

  if (loading) return <div className="text-center py-5"><Spinner /></div>;

  return (
    <>
      <h3 className="mb-4">My Classes</h3>
      {msg.text && <Alert variant={msg.type} dismissible onClose={() => setMsg({ type: '', text: '' })}>{msg.text}</Alert>}

      {rows.length === 0 ? (
        <Alert variant="info">You haven't hired any teacher yet. Book a demo and hire to start classes.</Alert>
      ) : (
        <Row>
          {rows.map((h) => (
            <Col md={6} key={h.id} className="mb-3">
              <Card className="shadow-sm h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between">
                    <h5>{h.teacher_name}</h5>
                    <Badge bg={h.status === 'active' ? 'success' : 'secondary'}>{h.status}</Badge>
                  </div>
                  <p className="text-muted small mb-2">{h.city} · Started {formatDate(h.start_date)}{h.end_date ? ` · Ended ${formatDate(h.end_date)}` : ''}</p>
                  <p className="mb-2">Fee: ₹{Number(h.monthly_fee).toFixed(0)}/month</p>
                  <p className="mb-3">
                    Classes: <strong>{h.classes_taken}</strong> taken ·{' '}
                    <strong>{h.classes_present}</strong> present
                  </p>
                  <div className="d-flex flex-wrap gap-2">
                    <Button size="sm" variant="outline-primary" onClick={() => setDetail(h.id)}>View attendance</Button>
                    {h.status === 'active' && (
                      <>
                        <Button size="sm" onClick={() => payNext(h.id)}>Pay next month</Button>
                        <Button size="sm" variant="outline-success" onClick={() => setReview({ hireId: h.id, teacherId: h.teacher_id, teacherName: h.teacher_name })}>Leave review</Button>
                        <Button size="sm" variant="outline-danger" onClick={() => endHire(h.id)}>End hire</Button>
                      </>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {detail && <HireDetailModal id={detail} onClose={() => setDetail(null)} />}
      {review && <ReviewModal meta={review} onClose={() => setReview(null)} onDone={() => { setReview(null); load(); }} />}
    </>
  );
}

function HireDetailModal({ id, onClose }) {
  const [h, setH] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.get(`/student/hires/${id}`).then(setH).catch((e) => setErr(e.message));
  }, [id]);

  return (
    <Modal show onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton><Modal.Title>Class detail</Modal.Title></Modal.Header>
      <Modal.Body>
        {err && <Alert variant="danger">{err}</Alert>}
        {!h && !err && <div className="text-center"><Spinner /></div>}
        {h && (
          <>
            <p className="mb-2"><strong>Teacher:</strong> {h.teacher_name}</p>
            <p className="mb-3"><strong>Started:</strong> {formatDate(h.start_date)} · <strong>Monthly fee:</strong> ₹{Number(h.monthly_fee).toFixed(0)}</p>
            <h6>Attendance</h6>
            {h.attendance?.length ? (
              <Table size="sm" bordered><thead><tr><th>Date</th><th>Status</th><th>Note</th></tr></thead>
                <tbody>{h.attendance.map((a) => <tr key={a.id}><td>{formatDate(a.date)}</td><td><Badge bg={a.status === 'present' ? 'success' : 'danger'}>{a.status}</Badge></td><td>{a.teacher_note || '-'}</td></tr>)}</tbody>
              </Table>
            ) : <p className="text-muted small">No classes marked yet.</p>}
            <h6 className="mt-3">Payments (monthly settlements)</h6>
            {h.settlements?.length ? (
              <Table size="sm" bordered><thead><tr><th>Period</th><th>Amount</th><th>Status</th></tr></thead>
                <tbody>{h.settlements.map((s, i) => <tr key={i}><td>{s.period_month}</td><td>₹{Number(s.gross).toFixed(0)}</td><td><Badge bg={s.status === 'paid' ? 'success' : 'warning'}>{s.status}</Badge></td></tr>)}</tbody>
              </Table>
            ) : <p className="text-muted small">No payments yet.</p>}
          </>
        )}
      </Modal.Body>
      <Modal.Footer><Button onClick={onClose} variant="secondary">Close</Button></Modal.Footer>
    </Modal>
  );
}

function ReviewModal({ meta, onClose, onDone }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await api.post('/student/reviews', { teacher_id: meta.teacherId, rating, comment });
      onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton><Modal.Title>Review {meta.teacherName}</Modal.Title></Modal.Header>
      <Modal.Body>
        {err && <Alert variant="danger">{err}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>Rating</Form.Label>
          <Form.Select value={rating} onChange={(e) => setRating(Number(e.target.value))}>
            {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} ★</option>)}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Comment</Form.Label>
          <Form.Control as="textarea" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={busy}>{busy ? 'Submitting...' : 'Submit review'}</Button>
      </Modal.Footer>
    </Modal>
  );
}
